import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { Actions } from '@ngrx/effects';
import {
    PageService,
    SePageComponent,
    UploadExternalData
} from '@se/platform';
import { RouterStackService, RouterDataService } from '@se/se.ui.html5';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { LoadedDataFromRoute, UploadInfo } from '../../models';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { VnaErrorMatcher } from "../../utils/vna-error-matcher";
import { HelperService } from '../../utils/helper.service';
import { OriginPage, BusinessUnit } from './../../models';
import { OrderBusinessUnitService } from '../../order-business-unit.service';
import { MatAccordion } from '@angular/material/expansion';

@Component({
    selector: 'toolupload-form',
    templateUrl: './toolupload-form.component.html',
    styleUrls: ['./toolupload-form.component.scss']
})

export class TooluploadFormComponent extends SePageComponent implements OnInit, OnDestroy {
    @ViewChild(MatAccordion) accordion!: MatAccordion;

    @Input() vnaUploadExternalData?: UploadExternalData;

    externalPage: OriginPage;
    filesList: FileList;
    formData: FormGroup = null;
    loadedData: LoadedDataFromRoute;
    matcher: VnaErrorMatcher = new VnaErrorMatcher();
    options: Array<BusinessUnit>;
    uploadInfo?: UploadInfo;
    private actionsSubscription!: Subscription;
    private changeThemeSubs: Subscription;

    constructor(
        private actions: Actions,
        pageService: PageService,
        private routerStackService: RouterStackService,
        private routerDataService: RouterDataService,
        public datePipe: DatePipe,
        private fb: FormBuilder,
        private helper: HelperService,
        private orderBusiness: OrderBusinessUnitService
    ) {
        super(pageService);
        this.options = [];
    }

    ngOnInit() {
        this.manageData();
        if (this.externalPage !== OriginPage.EXTERNAL_NOT_DICOM) {
            this.getBusinessOrderList();
        }
    }

    ngOnDestroy() {
        if (this.actionsSubscription)
            this.actionsSubscription.unsubscribe();
        if (this.changeThemeSubs && !this.changeThemeSubs.closed) {
            this.changeThemeSubs.unsubscribe();
        }
    }

    manageData(): void {
        if (this.routerDataService && this.routerDataService.data?.from === OriginPage.EXTERNAL_NOT_DICOM) {
            this.externalPage = OriginPage.EXTERNAL_NOT_DICOM;
            let information: UploadInfo = this.routerDataService.data.UploadImagesData[0];
            this.loadedData = this.helper.configurationDataFromThirdPartiesNotDimcom(information);
        } else {
            this.loadedData = this.helper.configurationDataEstensa(this.vnaUploadExternalData);
        }
        this.uploadInfo = this.loadedData.uploadInfo;
        this.formData = this.buildVnaForm();
        if (this.externalPage !== OriginPage.EXTERNAL_NOT_DICOM) {
            let fgOrderData = this.formData.controls['orderData'] as FormGroup;
            fgOrderData.addControl('orderBusinessUnit', this.businessUnitField());
        }
    }

    buildVnaForm(): FormGroup {
        return this.fb.group({
            patientData: this.fb.group({
                issuer: [{
                    value: this.uploadInfo.Issuer,
                    disabled: true
                }],
                patientId: this.formDataBuilderRequired(this.uploadInfo, 'PatientId'),
                surname: this.formDataBuilderRequired(this.uploadInfo, 'Surname'),
                name: this.formDataBuilderRequired(this.uploadInfo, 'Name'),
                sex: this.formDataBuilderRequired(this.uploadInfo, 'Sex'),
                taxCode: this.formDataBuilderOptional(this.uploadInfo, 'TaxCode'),
                birthDate: this.formDataBuilderRequired(this.uploadInfo, 'BirthDate'),
            }),
            orderData: this.fb.group({
                orderDescription: this.formDataBuilderRequired(this.uploadInfo, 'OrderDescription'),
                orderDate: this.formDataBuilderRequired(this.uploadInfo, 'OrderDate'),
                orderProvenance: this.formDataBuilderOptional(this.uploadInfo, 'OrderProvenance'),
                orderNumber: this.formDataBuilderOptional(this.uploadInfo, 'OrderNumber'),
            }),
            physicianData: this.fb.group({
                referringPhysicianName: this.formDataBuilderRequired(this.uploadInfo, 'ReferringPhysicianName'),
                referringPhysicianSurname: this.formDataBuilderRequired(this.uploadInfo, 'ReferringPhysicianSurname'),
            })
        });
    }

    showFormValidation(formGroup: string) {
        return this.formData.get(formGroup).touched ? true : false;
    }

    showFormError(formGroup: string) {
        return this.formData.get(formGroup).invalid ? true : false;
    }

    private businessUnitField(): FormControl {
        if (this.uploadInfo) {
            return !!this.uploadInfo.OrderBusinessUnit ?
                this.fb.control(
                    { value: this.uploadInfo.OrderBusinessUnit.description, disabled: true }
                ) : this.formDataBuilderRequired(this.uploadInfo, 'OrderBusinessUnit')
        }

    }

    private formDataBuilderRequired(source: any, fieldName: string): FormControl {
        return this.fb.control(
            {
                value: source[fieldName],
                disabled: this.manageFieldsAbilitation(source, fieldName),
            },
            [Validators.required]
        );
    }

    private formDataBuilderOptional(source: any, fieldName: string): FormControl {
        return this.fb.control(
            {
                value: source[fieldName],
                disabled: this.manageFieldsAbilitation(source, fieldName),
            }
        );
    }

    private manageFieldsAbilitation(source: any, fieldName: string): boolean {
        return !!source[fieldName];
    }

    isDisabledAndNotEmpty(source: string, controlName: string): boolean {
        let nestedFG = this.formData.controls[source] as FormGroup;
        return nestedFG.controls[controlName].disabled && nestedFG.controls[controlName].value;
    }

    private getBusinessOrderList() {
        this.orderBusiness.getBusinessUnitsFromAll().subscribe((items) => {
              this.options = [...items];
        })  
    }

    onFileUploaded(inputEvent: FileList) {
        this.filesList = inputEvent;
    }
}
